import { useDate } from '@/composables/useDate.ts'

const { getMomentByTime, moment } = useDate()

export function useTimeslotHelpers() {

  // Expects time slots in the format the device manager api returns:
  // 11:30-13:45,16:00-19:25,23:00-23:59
  // and returns an array of time slot objects.
  function parseTimeSlots(raw) {
    if (raw.length < 1) {
      return []
    }
    return raw.split(',').map((slot) => parseTimeSlot(slot))
  }

  function parseTimeSlot(raw) {
    const parts = raw.split('-')
    return {
      start: parts[0],
      end: parts[1],
    }
  }

  function stringifyTimeSlot(slot) {
    return `${slot.start}-${slot.end}`
  }

  function stringifyTimeSlots(slots) {
    return slots.map((slot) => stringifyTimeSlot(slot)).join(',')
  }

  function canAddDeviceTimeSlotAfter(lastSlot) {
    if (!lastSlot) {
      return true
    }
    const lastPossibleSlotStart = moment().hour(23).minute(59).second(0)
    const endParts = lastSlot.end.split(':')
    return moment()
      .hour(endParts[0])
      .minute(endParts[1])
      .second(0)
      .isBefore(lastPossibleSlotStart)
  }

  function getNextDeviceTimeSlot(lastSlot) {
    const lastEnd = lastSlot ?
      moment(lastSlot.end, 'HH:mm') : moment().minute(0)

    let newStart = moment(lastEnd)
    if (lastEnd.hour() < 23) {
      newStart.hour(lastEnd.hour() + 1).minute(0)
    } else {
      newStart.minute(lastEnd.minute() + 1)
    }

    let newEnd = moment(newStart)
    if (newStart.hour() < 23) {
      newEnd.hour(newStart.hour() + 1)
    } else {
      newEnd.hour(23).minute(59)
    }

    return {
      start: newStart.format('HH:mm'),
      end: newEnd.format('HH:mm'),
    }
  }

  function validateTimeSlot(start, end, prevEnd = false, nextStart = false) {
    const startTime = getMomentByTime(start)
    const endTime = getMomentByTime(end)
    const prevEndTime = prevEnd
      ? getMomentByTime(prevEnd)
      : moment().hour(0).minute(0).second(0)
    const nextStartTime = nextStart
      ? getMomentByTime(nextStart)
      : moment().hour(23).minute(59).second(0)
    return (
      startTime.isBefore(endTime) &&
      prevEndTime.isSameOrBefore(startTime) &&
      nextStartTime.isSameOrAfter(endTime)
    )
  }

  function validateTimeSlots(slots) {
    slots = cloneTimeSlots(slots).sort(compareTimeSlots)
    return slots.reduce((result, slot, index) => {
      const prevSlot = index > 0 ? slots[index - 1] : false
      const nextSlot = index < slots.length - 1 ? slots[index + 1] : false
      return validateTimeSlot(
        slot.start,
        slot.end,
        prevSlot ? prevSlot.end : false,
        nextSlot ? nextSlot.start : false
      )
    }, true)
  }

  function compareTimeSlots(a, b) {
    // Basic sorting start time by converting the str to an int.
    // E.g.: "12:39" -> 1239 and comparing these values.
    const startA = parseInt(a.start.replace(/[^0-9]+/, ''))
    const startB = parseInt(b.start.replace(/[^0-9]+/, ''))
    return startA - startB
  }

  // Clone each slot so local edits don't mutate objects in the Pinia store directly.
  function cloneTimeSlots(slots) {
    return JSON.parse(JSON.stringify(slots))
  }

  function timeSlotsAreEqual(slotA, slotB) {
    return slotA.start === slotB.start && slotA.end === slotB.end
  }

  return {
    parseTimeSlots,
    parseTimeSlot,
    canAddDeviceTimeSlotAfter,
    getNextDeviceTimeSlot,
    stringifyTimeSlots,
    stringifyTimeSlot,
    validateTimeSlots,
    validateTimeSlot,
    compareTimeSlots,
    cloneTimeSlots,
    timeSlotsAreEqual,
  }
}